import React, { useState, useRef, useEffect, useCallback } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import RegisterPromptModal from './RegisterPromptModal';
import VoiceMode from './VoiceMode';
import AdvisorHeader from './AdvisorHeader';
import AdvisorNotice from './AdvisorNotice';
import useAdvisorContext from '../hooks/useAdvisorContext';
import { Sparkles, History } from 'lucide-react';
import api, { getAccessToken } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ChatContainer.css';

const defaultSuggestedChips = [
  '3BR in New Cairo under 6M',
  'Family compound near schools',
  'Ready for delivery 2025',
  'Payment plan up to 10 years',
];

const ChatContainer = ({ backendUrl, backendStatus, onRetryConnection, onFavoriteCompound, suggestedChips: suggestedChipsProp, sessionId: controlledSessionId, onSessionChange, loadedMessages, onSessionCreated, showHistoryButton, historyOpen, onToggleHistory, focus = null, focusLabel = null, onClearFocus, showHeader = true, inheritSchemaFrom = null }) => {
  const welcomeMessage = {
    id: 1,
    content: "Hi! I'm your AI property assistant. I'll help you find the right property by understanding your needs, budget, and preferences. What brings you here today—first home, investment, or something else?",
    sender: 'assistant',
    timestamp: new Date(),
  };

  // A screen that opens on a specific compound should not greet the client as
  // though they had just arrived — the pill and the chips already say what this
  // conversation is about.
  const [messages, setMessages] = useState(focus ? [] : [welcomeMessage]);
  // Whose advisor this is. Detected here only to send the identifier; the
  // company itself — name, logo, notice — is whatever the SERVER returns, so
  // the browser cannot decide which brand it wears.
  const advisorContext = useAdvisorContext();
  const [advisor, setAdvisor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(controlledSessionId ?? null);
  const [isFirstMessage, setIsFirstMessage] = useState(!controlledSessionId);
  const [debugMode, setDebugMode] = useState(false);
  const [suggestedChips, setSuggestedChips] = useState(suggestedChipsProp ?? defaultSuggestedChips);
  const [guestMessagesRemaining, setGuestMessagesRemaining] = useState(null);
  const [registerPrompt, setRegisterPrompt] = useState(null); // { title, message } | null
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const { isAuthenticated, silentRefresh } = useAuth();

  // Keep the newest message visible when the keyboard opens. The shell shrinks
  // by the keyboard's height (useKeyboardInset), so without this the thread
  // keeps its old scroll offset and the message the user is replying to slides
  // out of view behind the composer — on a short phone, exactly the message
  // they were reading.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
      });
    };
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, []);

  // When loadedMessages from history arrive, display them
  useEffect(() => {
    if (loadedMessages && loadedMessages.length > 0) {
      const mapped = loadedMessages.map((m, i) => ({
        id: m.id || i,
        content: m.content,
        sender: m.role === 'user' ? 'user' : 'assistant',
        timestamp: m.created_at ? new Date(m.created_at) : new Date(),
        searchResults: m.metadata?.search_results || null,
      }));
      setMessages(mapped);
      setIsFirstMessage(false);
      // A page that supplied its own chips (compound, compare) keeps them —
      // they are how the user learns what this screen can answer. Only the
      // generic chat drops them, where they'd be stale mid-conversation.
      setSuggestedChips(suggestedChipsProp ?? []);
      // Jump, don't glide: smooth-scrolling a freshly loaded thread animates
      // past the whole history and can land mid-transcript.
      requestAnimationFrame(() => messagesEndRef.current?.scrollIntoView({ block: 'end' }));
    }
  }, [loadedMessages, suggestedChipsProp]);

  // When controlled session clears (new chat), reset state
  useEffect(() => {
    if (controlledSessionId === null) {
      setSessionId(null);
      setIsFirstMessage(true);
      // Same rule as the initial state: a screen that names its subject should
      // not greet the client as a stranger.
      setMessages(focus ? [] : [welcomeMessage]);
      setSuggestedChips(suggestedChipsProp ?? defaultSuggestedChips);
    } else {
      setSessionId(controlledSessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledSessionId, focus]);

  useEffect(() => {
    if (onSessionChange && sessionId) onSessionChange(sessionId);
  }, [sessionId, onSessionChange]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || isLoading) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      content: messageText,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      let response;
      // Chat posts with raw fetch rather than the Axios instance, so it does
      // NOT get services/api.js's silent-refresh interceptor. That gap ended
      // real conversations: when a token expired mid-chat the request came
      // back unauthenticated and the conversation simply stopped, with the
      // user mid-answer. Refresh once and retry here, matching what the
      // interceptor does for every other call.
      const postWithRefresh = async (path, body) => {
        const send = () => {
          const token = getAccessToken();
          return fetch(`${backendUrl}${path}`, {
            method: 'POST',
            credentials: 'include', // send the HttpOnly refresh cookie
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            // focus is sent explicitly on every turn, null included: the server
            // treats an OMITTED focus as "keep whatever you had", which is the
            // voice bridge's contract. A screen always knows what it is showing.
            body: JSON.stringify({ ...body, focus }),
          });
        };
        const first = await send();
        if (first.status !== 401) return first;
        const refreshed = await silentRefresh();
        if (!refreshed) return first;  // genuinely signed out — let the caller handle it
        return send();
      };

      if (isFirstMessage) {
        // Start new chat session
        response = await postWithRefresh('/api/chat/start', {
          initial_message: messageText, debug: debugMode,
          ...(advisorContext.slug ? { advisor_slug: advisorContext.slug } : {}),
          // The API is on a different domain, so the server cannot see which
          // branded host served this page. Report it; the server resolves it.
          ...(advisorContext.label ? { advisor_subdomain: advisorContext.label } : {}),
          // New thread, same buyer: carry the requirement schema over so the
          // agent does not re-ask budget and area it already knows.
          ...(inheritSchemaFrom ? { inherit_schema_from: inheritSchemaFrom } : {}),
        });
        // Note: isFirstMessage is flipped to false only after a successful
        // response below, so a failed/quota-blocked start can be retried as a start.
      } else {
        // Continue existing conversation
        response = await postWithRefresh('/api/chat/message', {
          session_id: sessionId, message: messageText, debug: debugMode,
        });
      }

      // Guest quota exceeded — prompt registration instead of a generic error
      if (response.status === 429) {
        let data = null;
        try { data = await response.json(); } catch (_) { /* ignore */ }
        if (data?.detail?.reason === 'guest_quota') {
          const scope = data.detail.scope;
          const title = scope === 'daily_conversations'
            ? 'You\u2019ve reached today\u2019s guest limit'
            : 'Register to keep chatting';
          setRegisterPrompt({
            title,
            message: data.detail.message || 'Create a free account to continue your conversation.',
          });
          // Roll back the optimistic user message and stop cleanly
          setMessages(prev => prev.filter(m => m.id !== userMessage.id));
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Successful turn — subsequent messages continue the conversation.
      setIsFirstMessage(false);

      // Co-branding, sent by the server. Null on ordinary marketplace chats.
      if (data.advisor) setAdvisor(data.advisor);

      // Track guest quota for a subtle inline hint
      if (data.is_guest && typeof data.guest_messages_remaining === 'number') {
        setGuestMessagesRemaining(data.guest_messages_remaining);
      } else {
        setGuestMessagesRemaining(null);
      }
      
      // Store session ID
      if (data.session_id) {
        setSessionId(data.session_id);
        // Notify parent so the sidebar can show the new session
        if (onSessionCreated && !sessionId) {
          onSessionCreated({ id: data.session_id, title: messageText.slice(0, 60), updated_at: new Date().toISOString() });
        }
      }

      // Add assistant response (ensure we have displayable content)
      const content = (data.assistant_message && data.assistant_message.trim()) 
        ? data.assistant_message.trim() 
        : (data.search_results?.results?.length 
          ? "Here are some properties that match your criteria." 
          : "Got it. How else can I help?");
      const assistantMessage = {
        id: Date.now() + 1,
        content,
        sender: 'assistant',
        timestamp: new Date(),
        userSchema: data.user_schema,
        searchResults: data.search_results,
        schemaComplete: data.schema_complete,
        inference: data.inference || null,
        agentError: data.agent_error || null,
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      // Always update chips from response so we don't show stale/wrong chips (use [] when null or missing)
      setSuggestedChips(Array.isArray(data.suggested_messages) ? data.suggested_messages : []);

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        content: `Sorry, I'm having trouble connecting to the backend. Error: ${error.message}. Please make sure the server is running on ${backendUrl}`,
        sender: 'assistant',
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Voice mode needs an existing session_id (the WS auth handshake looks up
  // an existing session, it doesn't create one — see Broker/voice_ws.py).
  // If the mic is tapped before any text message, bootstrap a session the
  // same way the first text message would (isFirstMessage branch above).
  //
  // Voice is authenticated-only (v1 scope) — guests get REST chat with a
  // quota, not voice. Check this BEFORE attempting anything, otherwise a
  // guest's mic tap silently opens a WebSocket that the server immediately
  // rejects (no JWT to authenticate), landing them in a bare "lost
  // connection" error with no explanation.
  const openVoiceMode = useCallback(async () => {
    if (!isAuthenticated) {
      setRegisterPrompt({
        title: 'Voice chat needs an account',
        message: 'Create a free account to talk to your AI property assistant instead of typing.',
      });
      return;
    }
    if (sessionId) {
      setVoiceModeOpen(true);
      return;
    }
    setIsLoading(true);
    try {
      const token = getAccessToken();
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const response = await fetch(`${backendUrl}/api/chat/start`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          initial_message: 'Hi', debug: debugMode, focus,
          ...(advisorContext.slug ? { advisor_slug: advisorContext.slug } : {}),
          // The API is on a different domain, so the server cannot see which
          // branded host served this page. Report it; the server resolves it.
          ...(advisorContext.label ? { advisor_subdomain: advisorContext.label } : {}),
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const data = await response.json();

      setIsFirstMessage(false);
      if (data.session_id) {
        setSessionId(data.session_id);
        if (onSessionCreated) {
          onSessionCreated({ id: data.session_id, title: 'Voice chat', updated_at: new Date().toISOString() });
        }
      }
      const content = (data.assistant_message && data.assistant_message.trim())
        ? data.assistant_message.trim()
        : 'Hi! How can I help?';
      setMessages(prev => [...prev, {
        id: Date.now(),
        content,
        sender: 'assistant',
        timestamp: new Date(),
        userSchema: data.user_schema,
        searchResults: data.search_results,
      }]);
      setSuggestedChips(Array.isArray(data.suggested_messages) ? data.suggested_messages : []);
      setVoiceModeOpen(true);
    } catch (error) {
      console.error('Failed to start a session for voice:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, sessionId, backendUrl, debugMode, onSessionCreated, focus,
      advisorContext.slug, advisorContext.label]);

  // Voice turns append to the same messages array text turns use, so they
  // interleave in one conversation view once voice mode closes.
  //
  // These append on turn_committed — the server event that fires only after
  // the pair is written to chat_messages — and not on the live caption
  // events. Appending from the captions was the transcript-desync bug: the
  // server drops a turn it decides is the assistant hearing itself, but the
  // caption had already been pushed, so the chat kept a turn the database
  // never had and the two only disagreed visibly after a reload.
  const handleVoiceTurnCommitted = useCallback(({ user_text: userText, assistant_text: assistantText }) => {
    setMessages(prev => {
      const next = [...prev];
      const now = Date.now();
      if (userText) next.push({ id: now, content: userText, sender: 'user', timestamp: new Date() });
      if (assistantText) next.push({ id: now + 1, content: assistantText, sender: 'assistant', timestamp: new Date() });
      return next;
    });
  }, []);

  // Closing voice re-reads the conversation from the server rather than
  // trusting what this component accumulated during the call. Committed turns
  // already keep the two in step; this is the backstop for anything that got
  // lost to a dropped socket mid-turn, and it is what makes "what you see
  // after the call" and "what a reload shows" the same thing by construction.
  const handleVoiceClose = useCallback(async () => {
    setVoiceModeOpen(false);
    if (!sessionId || !isAuthenticated) return;
    try {
      const { data } = await api.get(`/api/history/sessions/${sessionId}`);
      if (Array.isArray(data?.messages) && data.messages.length > 0) {
        setMessages(data.messages.map((m, i) => ({
          id: m.id || i,
          content: m.content,
          sender: m.role === 'user' ? 'user' : 'assistant',
          timestamp: m.created_at ? new Date(m.created_at) : new Date(),
          searchResults: m.metadata?.search_results || null,
        })));
      }
    } catch (err) {
      // Keep the local view rather than blanking the conversation; it is the
      // committed-turn view, which is already correct in the normal case.
      console.error('Failed to re-sync transcript after voice', err);
    }
  }, [sessionId, isAuthenticated]);

  return (
    <div className="chat-container">
      {/* A company's advisor wears the company's header, not ours. Driven by
          server-supplied branding, so it appears only once the server has
          confirmed which company this is. */}
      {advisor ? (
        <AdvisorHeader
          companyName={advisor.company_name}
          logoUrl={advisor.logo_url}
          whatsappUrl={advisor.whatsapp_url}
        />
      ) : showHeader && (
      <div className="chat-header">
        <div className="chat-header-broker">
          {showHistoryButton && (
            <button
              type="button"
              className={`chat-history-btn${historyOpen ? ' active' : ''}`}
              onClick={onToggleHistory}
              aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'}
              aria-pressed={!!historyOpen}
              title="Chat history"
            >
              <History size={20} />
            </button>
          )}
          <span className="chat-broker-avatar">
            <Sparkles size={18} color="#E8A020" />
          </span>
          <div className="chat-header-title">
            <h1>AI Broker</h1>
            <button
              type="button"
              className={`chat-header-status${backendStatus === 'disconnected' ? ' clickable' : ''}`}
              onClick={backendStatus === 'disconnected' ? onRetryConnection : undefined}
            >
              <span className={`chat-status-dot ${backendStatus}`} aria-hidden />
              <span className="chat-status-text">
                {backendStatus === 'connected'
                  ? 'Online · replies instantly'
                  : backendStatus === 'checking'
                  ? 'Connecting…'
                  : 'Offline · tap to retry'}
              </span>
            </button>
          </div>
          <label className="chat-debug-toggle" title="Show inference details">
            <input
              type="checkbox"
              checked={debugMode}
              onChange={(e) => setDebugMode(e.target.checked)}
              aria-label="Show inference details for each message"
            />
            <span>Debug</span>
          </label>
        </div>
      </div>
      )}

      <MessageList 
        messages={messages} 
        isLoading={isLoading}
        onFavoriteCompound={onFavoriteCompound}
        endRef={messagesEndRef}
        emptyLabel={focus ? focusLabel : null}
      />

      {guestMessagesRemaining !== null && guestMessagesRemaining <= 3 && (
        <div className="guest-quota-hint">
          {guestMessagesRemaining > 0
            ? `${guestMessagesRemaining} message${guestMessagesRemaining === 1 ? '' : 's'} left as a guest — register to keep your chat.`
            : 'Register to keep your chat going.'}
        </div>
      )}

      {/* Above the composer, and never dismissible: the moment it matters most
          is when they type a phone number, which is rarely the first message. */}
      {advisor?.privacy_notice && <AdvisorNotice text={advisor.privacy_notice} />}

      <MessageInput
        onSendMessage={sendMessage}
        disabled={isLoading || backendStatus === 'disconnected'}
        suggestedChips={suggestedChips}
        focusLabel={focus ? focusLabel : null}
        onClearFocus={onClearFocus}
        onOpenVoice={openVoiceMode}
      />

      <RegisterPromptModal
        open={!!registerPrompt}
        onClose={() => setRegisterPrompt(null)}
        title={registerPrompt?.title}
        message={registerPrompt?.message}
      />

      {voiceModeOpen && (
        <VoiceMode
          sessionId={sessionId}
          onClose={handleVoiceClose}
          onTurnCommitted={handleVoiceTurnCommitted}
        />
      )}
    </div>
  );
};

export default ChatContainer;
